import { Component, DetailEvent, Entity, registerComponent, registerSystem, System } from "aframe";
import { ConnectionComponent, ConnectionEvent } from "./connection";
import { SlotComponentData } from "./slot";
import { Variables, VariablesComponent } from "./variables";



const evaluate = (evaluator : Function, el : Entity, srcs : ScriptSource[], event? : Event ) =>
{
	let variables : Variables = {};
	for(let x = el; x; x = x.parentEl!)
	{
		const variablesComp = x.components.variables as VariablesComponent;
		if(variablesComp && variablesComp.data)
		{
			variables = variablesComp.evaluate();
			break;
		}
	}

	let source : Variables = {};
	const sources : Variables[] = [];
	for(let src of srcs)
	{
		if( src.source === el )
			continue; //Put "own" variables only in "this", not in "source"

		let sv : Variables = {};
		//Go up the AFrame Entity tree, to the first one with Variables
		for(let x = src.source as Entity; x; x = x.parentEl!)
		{
			const v = x.components.variables as VariablesComponent;
			if(v && v.data)
			{
				sv = v.evaluate(); //Will go up the tree, taking inheritance into account
				break;
			}
		}
		sources.push(sv);


		//Aggregate with other sources
		for(let key in sv)
		{
			const value = sv[key];
			if(key in source)
			{
				const current = source[key];
				const vtype = typeof value, ctype = typeof current;
				if((vtype == "number") && (ctype == "number"))
				{
					//Get the largest value from all bindings
					// TODO : allow another aggregation method (sum, average...)
					if(current! >= value!) continue;
				}
				else if(current !== null) //Everything replaces null
					continue; //Can't aggregate
			}
			source[key] = value;
		}
	}

	return evaluator.call(variables, source, sources, event, el);
	
}






export interface ScriptData
{
	event : string[];
	source? : Entity[]; //Static bindings to other Entities
	connector? : Entity[]; //Dynamic bindings to other Entities
	connection : string[]; //Attributes for connector
	target? : Entity[];
	attribute : string;
	property? : string;
	script? : string;
	value? : any;
	bubbling? : boolean;
	capture? : boolean;
}

interface ScriptSource
{
	source : Entity;
	eventSource : Entity;
	reset : () => void;
}

type Evaluator = (event? : Event) => any;

interface ScriptComponent extends Component
{
	system : ScriptSystem;

	data : ScriptData;
	sources : ScriptSource[];
	listener : (event? : Event) => any;
	evaluator? : Evaluator | null;

	connectorListener : EventListener;

	addSource(source: Entity, connection? : ConnectionComponent | undefined) : ScriptSource;
	addConnectedSource(connected : Entity, connector : Entity, connection : ConnectionComponent) : void;
}


const box = (value : any) =>
{
	const type = typeof value;
	if(type == "string") return new String(value);
	//if(type == "number") return new Number(value);
	//if(type == "boolean") return new Boolean(value);
	return value;
}
const unbox = (value : any) =>
{
	if(value instanceof String) return value.valueOf();
	if(value instanceof Number) return value.valueOf();
	if(value instanceof Boolean) return value.valueOf();
	return value;
}

const prepare = (value : any, attr : string, prop? : string) =>
{
	if(attr) //Setting a single attribute
	{
		if(prop) //Setting a single property
			return { [prop] : box(value) };

		if(value && (value.constructor === Object)) //Setting multiple properties with a dictionary
		{
			const boxed : any = {};
			for(let key in value)
				boxed[key] = box(value[key]);
			return boxed;
		}

		//Setting a single property attribute : don't box, actually unbox!
		return unbox(value);
	}
	
	if(value && (value.constructor === Object)) //Setting multiple attributes with a dictionary
	{
		const boxed : any = {};
		for(let att in value)
			boxed[att] = prepare(value[att], att);
		return boxed;
	}
	
	return value;
}



interface ScriptSystem extends System
{
	cache : { [code : string] :Function };
	compile(code : string, comment? : string) : Promise<Function>;
}

const statementRegex = /^\s*{.*(\breturn\b|;).*}\s*$/s;

registerSystem("script", {
	init(this : ScriptSystem)
	{
		this.cache = {};
	},
	compile(this : ScriptSystem, code : string, comment? : string)
	{
		return new Promise<Function>( (resolve, reject) => {
			const cached = this.cache[code];
			if(cached)
				return resolve(cached);

			if( ! statementRegex.test(code) )
				code = "{ return " + code + "; }";

			let source = "(() => {";
			if(comment)
				source += "/* " + comment + " */\n";
			source += "const compile = (window, document, navigator) => function(source, sources, event, el ) { with(this) with(source) " + code + " };";
			source += "document.currentScript.dispatchEvent(new CustomEvent('compiled', { detail : compile() }));";
			source += "})()";

			const script = this.el.ownerDocument.createElement("script");
			script.addEventListener("compiled", (event) =>
			{
				script.remove();
				const compiled = (event as DetailEvent<Function>).detail;
				this.cache[code] = compiled;
				resolve( compiled );
			}, { once : true } );
			script.append(source);
			this.el.ownerDocument.head.append(script);
		});
	}

});

/* Examples : 

- Copy variable A from S to this variable B, assuming S *has* variable A :
	script = "source: S; property: B; script: A; "

- Copy variable A from S to this variable B :
	script = "source: S; property: B; script: source.A; "

- Copy variable A from item inside slot S to this variable B :
	script = "slot: S; property: B; script: source.A; "

- Copy variable A from slot containing anchor X to this variable B :
	script = "anchor: X; property: B; script: source.A; "

- Toggle variable X when a button is activated in item inside slot S
	script = "slot: S; event: activate; property: X; script: 1-X; "

- Change this object's material according to this variables A and B
	script = "attribute:material; script:{ opacity : (A ? 1 : 0.5), emissive : (B ? '#AAA' : '#000' ) };"

*/


const validKeyRegex = /^[a-zA-Z_\$][0-9a-zA-Z_\$]*$/;

const capitalize = (word : string) => word.slice(0,1).toUpperCase() + word.slice(1).toLowerCase();
const toDatasetKey = (attrName : string) =>
{
	const words = attrName.split("-");
	if(words.length <= 1) return attrName;
	return words[0] + words.slice(1).map(capitalize).join("");
}

const parseScript = (value : any) => 
{
	if(!value) return "";
	if( (typeof value == "object") )
	{
		if( Array.isArray(value) )
			return value.join("\n");
		
		if( value.constructor === Object ) //Dictionary : take each key as a script
		{
			let script = "{", sep = "";
			for(let key in value)
			{
				script += sep;
				script += validKeyRegex.test(key) ? key :  ("\'" + key + "\'");
				script += " : ";
				script += parseScript( value[key] );
				sep = ",\n";
			}
			return script + "}";
		}
	}
	return String(value);
};

registerComponent("script", {
	
	schema : {
		event : { type : "array", default : ["variables-changed", "inherited-variables-changed"] }, //Event(s) that will trigger updates.
		bubbling : { type : "boolean", default : true },
		capture : { type : "boolean", default : false },
		source : { type : "selectorAll" }, //Direct reference(s) to source(s)
		connector : { type : "selectorAll" }, //References to child connector Entities ; Entities connected to these will be sources.
		connection : { type : "array" }, //Name of attributes of connector Entities to consider
		target : { type : "selectorAll" }, //References to entities that will get their attributes updated.
		attribute : { type : "string", default : "variables" }, //Which attribute to update.
		property : { type : "string" }, //Which attribute's property to update.
		script : { //JS code that will give the new value
			type : "string",
			parse : parseScript,
			stringify : (value : any) => String(value)
		}, 
		value : {  //Constant value 
			parse : (value : any) => {
				//Unbox : 
				if( value instanceof String ) return String(value);
				if( value instanceof Number ) return Number(value);
				if( value instanceof Boolean ) return Boolean(value);
				return value;
			},
			stringify : (value : any) => JSON.stringify(value),
		}
	},

	init(this: ScriptComponent)
	{
		const el = this.el;
		
		this.sources = [];
		
		this.connectorListener = ( (event : ConnectionEvent ) => {

			/* event.target becomes connected with event.detail.entity through event.detail.connection */

			if(!(this.data.connector && this.data.connector.includes(event.target)))
				return; //Not mine.

			if(event.detail.connection.el !== event.target)
				return; //Connection TO connector, not a connection OF connector.
			
			if(this.data.connection && this.data.connection.length)
			{
				const connComp = event.detail.connection;
				if(!this.data.connection.includes( connComp.attrName || connComp.name ))
					return; //Not this kind of connection
			}
			
			this.addConnectedSource(event.detail.entity, event.target as Entity, event.detail.connection);
			this.listener(event);

		} ) as EventListener;
		
		
		this.listener = async (event? : Event) =>
		{
			if(this.evaluator === undefined)
				return; //Wait for compilation

			if(!this.data.bubbling) //Only trigger when event is AT_TARGET
			 	if( (event && event.eventPhase) != Event.AT_TARGET )
					return; //Ignore bubbling events

			if(event && !this.data.event.includes(event.type))
			{
				if(!this.data.event.includes("variables-changed"))
					return; //Do not respond to this event.
				event = undefined;
			}
			
			const value = this.evaluator ? this.evaluator(event) : this.data.value;
			const valueType = typeof value;
			const targets = this.data.target || [ el ], attrib = this.data.attribute, prop = this.data.property;

			let targetValue = value;
			if(valueType != "function") //Not a function : prepare once
			{
				if(targetValue instanceof window.Promise)
					targetValue = await targetValue;
				targetValue = prepare(targetValue, attrib, prop);
			}

			for(let target of targets)
			{
				if( (target !== el) && !target.isConnected)
				{
					console.warn("Script target disconnected");
					continue;
				}
				if(valueType == "function")
				{
					targetValue = (value as Function).call(target);
					if(targetValue instanceof window.Promise)
						targetValue = await targetValue;
					if(targetValue === undefined) //Function just wanted to be called, nothing to set.
						continue;
					targetValue = prepare(targetValue, attrib, prop);
				}

				if(attrib)  //Setting a single attribute
				{
					target.setAttribute(attrib, targetValue);
				}
				else if(targetValue && (targetValue.constructor === Object)) //Setting multiple attributes
				{
					for(let attr in targetValue)
					{
						const value = targetValue[attr];
						if(value === null)
							target.removeAttribute(attr);
						else if(value !== undefined)
							target.setAttribute(attr, value);
					}
				}
			}

		};
	},

	addSource(this : ScriptComponent, source : Entity, connection? : ConnectionComponent )
	{
		const events = Array.from(this.data.event), capture = this.data.capture;

		if(source.sceneEl === source) //No inheritance at root level.
		{
			const ix = events.indexOf("inherited-variables-changed");
			if(ix >= 0)
				events.splice(ix, 1);
		}

		// Hack : for slots, listen to events at item level instead of anchor. Anchor is a child of item, so events will go up 
		let eventSource : Entity = source;
		if(connection && (connection.name == "slot"))
		{
			const slotData = connection.data as SlotComponentData;
			if(slotData && (slotData.anchor === source) && slotData.item)
				eventSource = slotData.item;
		}
		
		const ss : ScriptSource = { 
			source,
			eventSource,
			reset : () =>
			{
				const ix = this.sources.indexOf(ss);
				if(ix < 0) return;
				this.sources.splice(ix, 1);
				for(let event of events)
					event && eventSource.removeEventListener(event, this.listener, { capture } );
				if(events.includes("inherited-variables-changed"))
				{
					const n = parseInt(eventSource.dataset.inheritedVariablesListeners ?? "1");
					if(n > 1)
						eventSource.dataset.inheritedVariablesListeners = String(n-1);
					else
						delete eventSource.dataset.inheritedVariablesListeners;
				}
			} 
		};
		this.sources.push(ss);
		for(let event of events)
			event && eventSource.addEventListener(event, this.listener, { capture });
		if(events.includes("inherited-variables-changed"))
		{
			const n = parseInt(eventSource.dataset.inheritedVariablesListeners ?? "0");
			eventSource.dataset.inheritedVariablesListeners = String(n+1);
		}
		return ss;
	},

	addConnectedSource(this:ScriptComponent, connected : Entity, connector : Entity, connection : ConnectionComponent )
	{
		const ss = this.addSource(connected, connection);

		const disconnectedListener = ((event : ConnectionEvent ) =>
		{
			if(event.target !== connector) return;
			if(event.detail.entity !== connected) return;
			ss.reset();
			this.listener(event);
			connector.removeEventListener("disconnected-from-entity", disconnectedListener);
		}) as EventListener;
		connector.addEventListener("disconnected-from-entity", disconnectedListener);
	},

	multiple : true,

	remove(this : ScriptComponent)
	{
		if( this.data.connector )
			for(let entity of this.data.connector )
				entity.removeEventListener("connected-to-entity", this.connectorListener);
		
		while(this.sources.length)
			this.sources[0].reset();
	},
	
	update(this : ScriptComponent, oldData : ScriptData)
	{
		if( oldData.connector )
			for(let entity of oldData.connector )
				entity.removeEventListener("connected-to-entity", this.connectorListener);
		
		while(this.sources.length)
			this.sources[0].reset();

		const needsToCallListener = this.data.event.includes("variables-changed") || (this.data.event.length == 0) || this.data.event.includes('');
		const callListenerIfNeeded = needsToCallListener ? 	
		async () => {
			//Safe-guard : wait for all parents to start playing
			for(let el = this.el; el; el = el.parentEl!)
			{
				if(el.isPlaying) continue;
				await new Promise<Event>( (resolve, reject) => el.addEventListener("play", resolve, { once : true }));
			}
			this.listener();
		} : (() => {});

		if( (oldData.script != this.data.script) || (oldData.value != this.data.value) )
		{
			delete this.evaluator;

			//Script can be "hidden" in dataset because a-frame attributes can't hold any string (; separator)
			const code = this.el.dataset[ this.data.script || "" ] || this.data.script || this.el.dataset[ toDatasetKey(this.attrName!) ];
			if(code)
				this.system.compile(code, this.attrName).then( (compiled) =>
				{
					this.evaluator = (event) => {
						try {
							return evaluate(compiled, this.el, this.sources, event);
						}
						catch(err) {
							console.warn(err);
							console.dir({script: this, code});
						}
					};
					callListenerIfNeeded();
				} );
			else
				this.evaluator = null; //Just use value.
		}

		if( this.data.source )
			for(let src of this.data.source)
				this.addSource(src);
		
		if( this.data.connector )
			for(let con of this.data.connector)
			{
				con.addEventListener("connected-to-entity", this.connectorListener);

				for(let key in con.components)
				{
					const comp = con.components[key] as ConnectionComponent;
					if(!( comp.isEntityConnection && comp.getConnectedEntities ))
						continue; //Not a connection

					if( this.data.connection && this.data.connection.length )
					{
						if(!this.data.connection.includes(comp.attrName || comp.name))
							continue; //Not that kind of connection
					}

					const connected = comp.getConnectedEntities();
					for(let ent of connected)
						this.addConnectedSource(ent, con, comp);
				}
			}


		if(!(this.data.source || this.data.connector)) //Self-listen
			this.addSource(this.el);

		callListenerIfNeeded();
	}

});